window.AdminMedia = function AdminMedia({ showNotification }) {
  const [mediaList, setMediaList] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [fileTypeFilter, setFileTypeFilter] = React.useState('All');
  const [sourceFilter, setSourceFilter] = React.useState('All');
  const [isUploading, setIsUploading] = React.useState(false);
  const fileInputRef = React.useRef(null);

  React.useEffect(() => { fetchMedia(); }, []);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await window.apiClient.get('media.php');
      if (res.success) setMediaList(res.data || []);
    } catch (e) {
      setMediaList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);
        await window.apiClient.uploadFile('media.php', formData);
      }
      showNotification('Media files uploaded and auto-compressed successfully.');
      fetchMedia();
    } catch (err) {
      showNotification(err.message || 'File upload failed.', 'error');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    showNotification('File URL copied to clipboard!');
  };

  const handleDeleteMedia = async (id, name, source) => {
    if (source && source !== 'Uploaded') {
      showNotification('This image is managed by the ' + source + ' module. Delete it there.', 'error');
      return;
    }
    if (!window.confirm('Delete file ' + name + ' from library?')) return;
    try {
      await window.apiClient.delete('media.php', { id });
      showNotification('File deleted.');
      fetchMedia();
    } catch (err) {
      showNotification(err.message || 'Deletion failed.', 'error');
    }
  };

  const sourceTabs = React.useMemo(() => {
    const sources = new Set(mediaList.map(m => m.source || 'Uploaded'));
    return ['All', ...Array.from(sources)];
  }, [mediaList]);

  const filteredMedia = mediaList.filter((m) => {
    const ft = m.file_type || '';
    const fp = m.file_path || '';
    const matchesType = fileTypeFilter === 'All' ||
      (fileTypeFilter === 'images' && (ft.startsWith('image/') || fp.startsWith('data:image/'))) ||
      (fileTypeFilter === 'documents' && !ft.startsWith('image/') && !fp.startsWith('data:image/'));
    const mSource = m.source || 'Uploaded';
    const matchesSource = sourceFilter === 'All' || mSource === sourceFilter;
    const matchesSearch = (m.original_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      mSource.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSource && matchesSearch;
  });

  const sourceBadgeClass = (source) => {
    switch (source) {
      case 'Products':  return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'Catalogue': return 'bg-indigo-50 text-indigo-600 border-indigo-200';
      case 'Blogs':     return 'bg-green-50 text-green-600 border-green-200';
      case 'Uploaded':  return 'bg-orange-50 text-secondary border-orange-200';
      default:          return 'bg-slate-50 text-slate-500 border-slate-200';
    }
  };

  const isImage = (m) => {
    const ft = m.file_type || '';
    const fp = m.file_path || '';
    return ft.startsWith('image/') || fp.startsWith('data:image/');
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold text-slate-900">Media Library</h1>
          <p className="font-sans text-xs text-slate-500">Upload, browse, and manage images and documents used across the site.</p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="bg-secondary hover:bg-orange-600 disabled:opacity-60 text-white font-mono py-2.5 px-5 rounded font-bold uppercase tracking-wider shadow cursor-pointer text-xs"
          >
            {isUploading ? 'Uploading...' : 'Upload Files'}
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <input
            type="text"
            placeholder="Search by filename or source..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary text-xs"
          />
          <select
            value={fileTypeFilter}
            onChange={(e) => setFileTypeFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 outline-none focus:border-secondary text-xs"
          >
            <option value="All">All Types</option>
            <option value="images">Images</option>
            <option value="documents">Documents</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-2">
          {sourceTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setSourceFilter(tab)}
              className={`px-3 py-1.5 rounded-full border font-mono text-[10px] uppercase font-bold cursor-pointer transition-colors ${
                sourceFilter === tab
                  ? 'bg-secondary text-white border-secondary'
                  : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-secondary'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400 font-mono text-xs">Loading media library...</div>
      ) : filteredMedia.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 text-slate-400 font-mono text-xs">
          No media files found. Upload files to get started.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredMedia.map((item) => (
            <div key={item.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
              <div className="aspect-video bg-slate-50 flex items-center justify-center overflow-hidden">
                {isImage(item) ? (
                  <img src={item.file_path} alt={item.original_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="material-symbols-outlined text-4xl text-slate-300">description</span>
                )}
              </div>
              <div className="p-3 space-y-2 flex-1 flex flex-col">
                <p className="font-mono text-[10px] text-slate-800 font-bold truncate" title={item.original_name}>
                  {item.original_name}
                </p>
                <span className={`inline-block w-fit px-2 py-0.5 rounded border font-mono text-[9px] uppercase font-bold ${sourceBadgeClass(item.source || 'Uploaded')}`}>
                  {item.source || 'Uploaded'}
                </span>
                <div className="flex gap-2 pt-1 mt-auto">
                  <button
                    type="button"
                    onClick={() => handleCopyUrl(item.file_path)}
                    className="flex-1 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 font-mono py-1.5 rounded text-[9px] uppercase font-bold cursor-pointer"
                  >
                    Copy URL
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteMedia(item.id, item.original_name, item.source)}
                    className="flex-1 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 font-mono py-1.5 rounded text-[9px] uppercase font-bold cursor-pointer"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
